import { useQuery, useMutation, useQueryClient } from 'react-query';
import _ from 'lodash';
import * as Sentry from '@sentry/browser';
import copilot from '@janus.team/janus-copilot/cjs/janus-copilot';
import * as oauth from 'oauth';
import * as queries from 'hooks/queries';
import React from 'react';
import { useRackspaceAccountContext } from 'customer/context';
import * as config from 'config';
import { userPermissionsValidator, orgConstraintsValidator } from '../customerView/onBoarding/steps/RackspacePermissions/constraintsValidator';
import * as rackspace from '../api/rackspaceApi';
import * as gcp from '../api/googleApis';
import * as janus from '../api/janusGcp';

const idFromOrgName = (name) => {
  const [prefix, id = name] = _.split(name, '/');
  return id;
};

const getClaimableOrganizations = async () => {
  const fetchedData = await gcp.getGCPOrganizations();
  const gcpOrganizations = fetchedData?.organizations;
  if (gcpOrganizations?.length === 0) {
    return {
      claimableOrganizations: [], gcpOrganizations: [], organizationClaims: [],
    };
  }

  const gcpOrganizationIds = _.map(gcpOrganizations, (org) => idFromOrgName(org.name)).join(',');
  const organizationClaims = await janus.testOrganizationClaims(gcpOrganizationIds);
  const claimableOrganizationIds = _.get(organizationClaims, 'availableOrganizations', []);
  const claimableOrganizations = _.filter(gcpOrganizations, (gcpOrg) => _.includes(claimableOrganizationIds, idFromOrgName(gcpOrg.name)));
  return { claimableOrganizations, gcpOrganizations, organizationClaims };
};

const constraintsValidations = async (organizationDomain, requiredPermissions) => {
  const missingUserPermissionError = 'MissingUserPermission';
  const orgPolicyConstraintError = 'OrgPolicyConstraintSet';

  const { permissions } = await gcp.testIamPermissions(organizationDomain, requiredPermissions);
  const userPermissionsValidationStatus = userPermissionsValidator(permissions, requiredPermissions);
  if (userPermissionsValidationStatus) return missingUserPermissionError;

  const { policies } = await gcp.listOrgPolicies(organizationDomain);
  if (!policies) return false;

  const constraintToValidate = 'iam.allowedPolicyMemberDomains';
  const orgConstraintsValidationStatus = orgConstraintsValidator(policies, constraintToValidate);
  if (orgConstraintsValidationStatus) return orgPolicyConstraintError;

  return false;
};

const verifyIsOnboarded = async (rackspaceAccountNumber) => {
  const gcpOrganizations = _.get(await gcp.getGCPOrganizations().catch(() => null), 'organizations') || [];
  const gcpOrganizationIds = _.map(gcpOrganizations, (org) => idFromOrgName(org.name));
  const organizationClaims = await janus.testOrganizationClaims(gcpOrganizationIds).catch(() => null);
  const claimedOrganizations = _.get(organizationClaims, 'organizations', []);
  const isOnboarded = _.some(claimedOrganizations, (claim) => (
    (claim?.rackspaceAccountNumber === rackspaceAccountNumber)
      && _.includes(gcpOrganizationIds, claim?.organizationId)
  ));
  return { isOnboarded, gcpOrganizations, organizationClaims };
};

const getGCPIAMPolicy = async (organizationDomain) => {
  const gcpIamPolicy = await gcp.getIamPolicy(organizationDomain);
  if (gcpIamPolicy.length === 0) return [];
  return gcpIamPolicy;
};

export const useQueryAccessGrants = (rackspaceAccountNumber, options = {}) => useQuery(['access-grants', rackspaceAccountNumber], janus.getAccessGrants, { ...options });

export const useQueryIsOnboarded = (options = {}) => {
  const { email } = oauth?.getCurrentUser() || {};
  const rackspaceAccountNumber = _.get(React.useContext(copilot.context.session), 'session.session.identity.access.user.RAX-AUTH:domainId');
  return useQuery(['is-onboarded', `${rackspaceAccountNumber}`, `${email}`], () => verifyIsOnboarded(rackspaceAccountNumber), {
    ...options,
  });
};

export const useQueryGetGCPProjects = () => {
  const { rackspaceAccountNumber } = useRackspaceAccountContext();
  const projectsFilter = config.gcpProjectsFilter(rackspaceAccountNumber);
  const { data } = queries.useQueryIsOnboarded();
  const claimedOrganizationId = _.get(data, 'organizationClaims.organizations[0].organizationId', '');
  return useQuery(['gcp-projects'], () => gcp.getGCPProjects({ includeFolders: true, organizationIds: [claimedOrganizationId], filter: projectsFilter }), {
    enabled: !!data,
  });
};

export const useQueryGetSystemsAccount = (projectId) => {
  const queryOptions = {
    queryKey: ['systems-account', projectId],
    queryFn: () => rackspace.getSystemsAccount({ projectId }),
    enabled: !!projectId,
  };
  return useQuery(queryOptions);
};

const getRequiredQueryCriteriaForProjects = () => {
  const queryCriteria = config.isRacker() ? {} : {
    filter: {
      linkedState: ['LINK_REQUESTED', 'LINKED'],
    },
  };
  return queryCriteria;
};

export const useQueryGetProjectsFromJanus = (options = {}) => {
  const { rackspaceAccountNumber } = useRackspaceAccountContext();
  const queryCriteria = getRequiredQueryCriteriaForProjects();

  return useQuery({
    queryKey: ['janus-projects', rackspaceAccountNumber],
    queryFn: () => janus.getProjects(rackspaceAccountNumber, queryCriteria),
    refetchInterval: config.projectsPollingInterval() * 1000,
    ...options,
  });
};

export const useQueryGetUnmanagedProjects = (options = { enabled: true }) => {
  const { rackspaceAccountNumber } = useRackspaceAccountContext();
  const projectsFilter = config.gcpProjectsFilter(rackspaceAccountNumber);
  const { data } = queries.useQueryIsOnboarded(options);
  const claimedOrganizationId = _.get(data, 'organizationClaims.organizations[0].organizationId', '');
  const query = useQueryGetProjectsFromJanus(options);
  const managedProjectIds = _(query?.data?.projects || [])
    .reject((project) => _.includes(['UNLINKED', 'UNLINK_REQUESTED'], project.linkedState))
    .map('projectId')
    .value();
  return useQuery(['gcp-unmanaged-projects'], () => gcp.getUnmanagedProjects({ organizationIds: [claimedOrganizationId], managedProjectIds, filter: projectsFilter }), {
    ...options,
    enabled: !!options.enabled && !!data && query.isSuccess,
  });
};

export const useQueryGetOrganizationsFromJanus = (options = {}) => {
  const { rackspaceAccountNumber } = useRackspaceAccountContext();
  const queryStringCriteria = {
    include: ['billingInfo'],
  };

  return useQuery({
    queryKey: ['janus-organizations', rackspaceAccountNumber],
    queryFn: () => janus.getOrganizations(rackspaceAccountNumber, queryStringCriteria),
    ...options,
  });
};

export const useQueryGetClaimableOrganizations = (active) => {
  const googleAccountEmail = oauth?.getCurrentUser()?.email;
  return useQuery(['GetGcpOrganizations', googleAccountEmail], getClaimableOrganizations, {
    enabled: !!active,
  });
};

export const useQueryConstraintsValidations = (active, organizationDomain, requiredPermissions) => (
  useQuery(['PerformConstraintsValidations', organizationDomain], () => constraintsValidations(organizationDomain, requiredPermissions), {
    staleTime: 0,
    enabled: !!active,
  })
);

export const setIamOrgPolicy = async (orgPayload) => {
  const organizationDomain = _.get(orgPayload, 'googleOrganization.name');
  const gcpGetIamPolicy = await getGCPIAMPolicy(organizationDomain);
  const roles = gcp.extractRoles(orgPayload.selectedRoles);
  const gcpSetIamOrgPolicy = await gcp.setIamOrgPolicy(organizationDomain, gcpGetIamPolicy, roles);
  if (gcpSetIamOrgPolicy.length === 0) return [];
  return gcpSetIamOrgPolicy;
};

export const useQueryModifyPolicy = () => (
  useMutation(setIamOrgPolicy, {
    onError: (error) => {
      Sentry.captureException(error);
    },
    retry: 3,
  })
);

export const useQueryClaimJanusOrg = () => (
  useMutation((orgdata) => janus.claimOrganization(orgdata), {
    onError: (error) => {
      Sentry.captureException(error);
    },
    retry: 3,
  })
);

export const useQueryGetBillingConfig = (rackspaceAccountNumber, options = {}) => {
  const queryOptions = {
    queryKey: ['billing', rackspaceAccountNumber],
    queryFn: () => rackspace.getBillingConfiguration({ rackspaceAccountNumber }),
    ...options,
  };
  return useQuery(queryOptions);
};

export const useQueryPostGCPProjects = () => (
  useMutation((projects) => janus.postProjects(projects))
);

const setProject = (queryClient) => (newData) => queryClient.setQueryData(['janus-projects', newData.project.rackspaceAccountNumber], (oldData) => {
  const targetIndex = _.findIndex(oldData.projects, { projectId: newData.project.projectId });
  if (targetIndex === -1) {
    oldData.projects.push(newData.project);
  } else {
    oldData.projects[targetIndex] = newData.project;
  }
  return oldData;
});

export const useQueryPutServiceBlocks = () => {
  const queryClient = useQueryClient();

  return useMutation(janus.updateServiceBlocks, {
    onSuccess: setProject(queryClient),
  });
};

export const useQueryPutServiceLevel = () => {
  const queryClient = useQueryClient();

  return useMutation(janus.updateServiceLevel, {
    onSuccess: setProject(queryClient),
  });
};

export const useQueryModifyServices = ({ serviceModel }) => {
  const queryClient = useQueryClient();
  const modifyServices = serviceModel === 'standard'
    ? janus.updateServiceLevel
    : janus.updateServiceBlocks;

  return useMutation(modifyServices, {
    onSuccess: setProject(queryClient),
  });
};
